import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, regions, years } from '@/mocks/categories';
import { movies } from '@/mocks/movies';

const categoryNames: Record<string, string> = {};
categories.forEach((c) => {
  categoryNames[c.slug] = c.name;
});

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeRegion, setActiveRegion] = useState('all');
  const [activeYear, setActiveYear] = useState('all');
  const [activeSort, setActiveSort] = useState('default');

  const categoryName = slug ? categoryNames[slug] || '全部' : '全部';
  const searchQuery = searchParams.get('search') || '';
  const sortParam = searchParams.get('sort') || '';

  useEffect(() => {
    setActiveRegion('all');
    setActiveYear('all');
    setActiveSort(sortParam || 'default');
  }, [slug, sortParam]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug, activeRegion, activeYear, activeSort]);

  const filteredMovies = useMemo(() => {
    let result = [...movies];

    if (slug && slug !== 'all') {
      result = result.filter((m) => m.category === slug);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    if (activeRegion !== 'all') {
      result = result.filter((m) => m.region === activeRegion);
    }

    if (activeYear !== 'all') {
      if (activeYear === 'older') {
        result = result.filter((m) => parseInt(m.year) < 2022);
      } else {
        result = result.filter((m) => m.year === activeYear);
      }
    }

    if (activeSort === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (activeSort === 'new') {
      result.sort((a, b) => parseInt(b.year) - parseInt(a.year));
    } else if (activeSort === 'hot') {
      result.sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0));
    }

    return result;
  }, [slug, searchQuery, activeRegion, activeYear, activeSort]);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 小草影院`,
    description: `小草影院${categoryName}分类，海量${categoryName}资源免费在线观看，高清不卡顿秒开播。`,
    url: `https://xiaocaoyingyuan.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '小草影院',
      url: 'https://xiaocaoyingyuan.com/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                ...(searchQuery
                  ? [{ name: `${categoryName}`, path: `/category/${slug}` }, { name: `搜索: ${searchQuery}` }]
                  : [{ name: `${categoryName}` }]
                ),
              ]}
            />

            <div className="px-4 md:px-6 py-4">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                {searchQuery ? `搜索"${searchQuery}"的结果` : `${categoryName}`}
              </h1>
              <p className="text-sm text-foreground-500">
                {searchQuery
                  ? `找到 ${filteredMovies.length} 个相关资源`
                  : `小草影院${categoryName}分类，海量资源免费在线观看`}
              </p>
            </div>

            <div className="px-4 md:px-6 mb-6">
              <div className="flex flex-wrap items-center gap-2 p-1 bg-background-100 rounded-full">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-primary-500 hover:bg-background-200/70'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="px-4 md:px-6 mb-4 flex flex-wrap items-center gap-3">
              <span className="text-xs text-foreground-500 whitespace-nowrap">地区：</span>
              {[{ id: 'all', name: '全部' }, ...regions].map((r) => (
                <button
                  key={r.id}
                  onClick={() => setActiveRegion(r.id)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    activeRegion === r.id
                      ? 'bg-secondary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {r.name}
                </button>
              ))}
            </div>

            <div className="px-4 md:px-6 mb-6 flex flex-wrap items-center gap-3">
              <span className="text-xs text-foreground-500 whitespace-nowrap">年份：</span>
              {[{ id: 'all', name: '全部' }, ...years].map((y) => (
                <button
                  key={y.id}
                  onClick={() => setActiveYear(y.id)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    activeYear === y.id
                      ? 'bg-secondary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {y.name}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                {[
                  { id: 'default', name: '默认' },
                  { id: 'hot', name: '最热' },
                  { id: 'rating', name: '评分' },
                  { id: 'new', name: '最新' },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSort(s.id)}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all cursor-pointer whitespace-nowrap ${
                      activeSort === s.id
                        ? 'bg-accent-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-4 md:px-6 pb-12">
              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                  {filteredMovies.map((movie) => (
                    <Link
                      key={movie.id}
                      to={`/article/${movie.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                        <img
                          src={movie.coverImage}
                          alt={movie.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 text-xs font-semibold bg-accent-500 text-white rounded-md">
                            {movie.rating}
                          </span>
                        </div>
                        {movie.isNew && (
                          <div className="absolute top-2 right-2">
                            <span className="px-2 py-0.5 text-[10px] font-semibold bg-primary-500 text-white rounded-md">
                              NEW
                            </span>
                          </div>
                        )}
                      </div>
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {movie.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">
                        {movie.tags.slice(0, 2).join(' · ')}
                      </p>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-emotion-sad-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">没有找到相关资源，换个关键词试试吧</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}